/**
 * ThemeFeedbackCard — Feb 2026 (Modern Card Theme · Feedback Loop)
 *
 * Small, high-end reaction poll surfaced on the Modern Customer
 * Dashboard immediately after a customer opts into the new theme.
 * Purpose: capture qualitative sentiment AND reward the user with 50
 * loyalty points as a one-time thank-you for engaging with the rollout.
 *
 * Visibility rules (all must be true to render):
 *   1. Current theme is 'modern' (parent gates on isModern already,
 *      but we double-check here so the file is safe to reuse).
 *   2. User is a customer (backend GET returns eligible:true).
 *   3. User has not yet submitted (backend GET returns submitted:false).
 *
 * On submit:
 *   1. POST /api/users/me/theme-feedback { reaction }
 *   2. Server writes ui_feedback doc + awards 50 pts (source=theme_feedback)
 *   3. Client hides the card and shows a toast with "+50 pts"
 *   4. React Query points cache invalidated so the matte-gold points
 *      card on the same page tick-ups its balance in real time.
 *
 * Design language uses the Modern token ladder — Inter type, rounded
 * corners, soft shadow, warm off-white surface with a subtle emerald
 * radial wash behind the emoji reactions. The +50 pts hint is in
 * matte gold to match the points card next door.
 */
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useFlag } from '../context/FeatureFlagsContext';
import { toast } from 'sonner';
import { X, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const REACTIONS = [
  { key: 'love',         emoji: '😍', label: 'Love it!' },
  { key: 'okay',         emoji: '🧐', label: "It's okay." },
  { key: 'back_to_code', emoji: '📟', label: 'Take me back to the Code.' },
];

export function ThemeFeedbackCard({ onSubmitted } = {}) {
  const { user } = useAuth();
  const { isModern, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [selecting, setSelecting] = useState(null);  // key of reaction while POSTing

  // Guard: only for authenticated customers on the Modern theme.
  const pollOn = useFlag('customer.theme_feedback_poll', false);
  const enabled = !!(pollOn && user && user.id && user.role === 'customer' && isModern);

  const statusQuery = useQuery({
    queryKey: ['customer', 'theme-feedback-status'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/theme-feedback`, { withCredentials: true });
      return data || { submitted: false, eligible: false };
    },
    enabled,
    staleTime: Infinity,  // one-time poll, no need to refetch
  });

  const submitMutation = useMutation({
    mutationFn: async (reaction) => {
      const { data } = await axios.post(
        `${API_URL}/api/users/me/theme-feedback`,
        { reaction },
        { withCredentials: true },
      );
      return data;
    },
    onSuccess: (data) => {
      // Prime the status cache so the card hides immediately without a refetch.
      queryClient.setQueryData(['customer', 'theme-feedback-status'], {
        submitted: true, reaction: data.reaction, eligible: true,
      });
      // Nudge the points cache — the matte-gold Points card on the same
      // dashboard will pull the fresh balance.
      queryClient.invalidateQueries({ queryKey: ['customer', 'points'] });
      queryClient.invalidateQueries({ queryKey: ['customer', 'tier'] });
      // Parent hook `usePoints` is useState-based (not React Query) so
      // it needs a direct refresh callback to tick the points-balance card.
      if (typeof onSubmitted === 'function') {
        try { onSubmitted(); } catch (_) { /* best-effort UI refresh */ }
      }
      // ✨ Delight: honour the "📟 Take me back to the Code" request by
      // actually flipping the theme back to 'code' after the pts are in
      // the wallet. Small delay so the user sees the "+50 pts" toast fire
      // on the Modern surface before it fades out.
      if (data.reaction === 'back_to_code') {
        toast.success(`Thanks for the feedback — +${data.points_awarded || 50} pts added! Switching back to Code…`);
        setTimeout(() => { setTheme('code'); }, 900);
      } else {
        toast.success(`Thanks for the feedback — +${data.points_awarded || 50} pts added!`);
      }
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 409) {
        // Already submitted (race with another tab). Hide the card silently.
        queryClient.setQueryData(['customer', 'theme-feedback-status'], {
          submitted: true, reaction: null, eligible: true,
        });
      } else {
        toast.error('Could not submit feedback. Please try again.');
      }
      setSelecting(null);
    },
  });

  const handlePick = (reaction) => {
    if (submitMutation.isPending) return;
    setSelecting(reaction);
    submitMutation.mutate(reaction);
  };

  if (!enabled) return null;
  if (dismissed) return null;
  if (statusQuery.isLoading) return null;
  if (!statusQuery.data?.eligible) return null;
  if (statusQuery.data?.submitted) return null;

  return (
    <div
      className="am-card-lg"
      style={{
        padding: 20,
        marginBottom: 20,
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--am-card)',
      }}
      data-testid="theme-feedback-card"
    >
      {/* Soft emerald wash to visually link with the Welcome hero */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-60%', left: '-15%',
          width: 240, height: 240,
          background: 'radial-gradient(circle at center, var(--am-emerald-100) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Dismiss */}
      <button
        type="button"
        aria-label="Dismiss feedback card"
        onClick={() => setDismissed(true)}
        data-testid="theme-feedback-dismiss"
        style={{
          position: 'absolute',
          top: 12, right: 12,
          width: 28, height: 28,
          borderRadius: 999,
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          color: 'var(--am-ink-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--am-inset)'; e.currentTarget.style.color = 'var(--am-ink-2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--am-ink-3)'; }}
      >
        <X size={14} />
      </button>

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Reward hint pill (matte gold, small) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span
            className="am-tnum"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--am-gold-500)', color: 'var(--am-ink-inv)',
              fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 'var(--am-radius-pill)',
              boxShadow: 'var(--am-shadow-gold)',
            }}
          >
            <Sparkles size={10} /> +50 pts reward
          </span>
        </div>

        <p
          style={{
            margin: '0 0 4px',
            fontSize: 16, fontWeight: 600,
            color: 'var(--am-ink-1)', letterSpacing: '-0.01em',
            lineHeight: 1.35,
          }}
          data-testid="theme-feedback-question"
        >
          Help us grow! <span aria-hidden>🌿</span>
        </p>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--am-ink-2)' }}>
          How do you like the new Ash &amp; Merch look?
        </p>

        <div
          role="radiogroup"
          aria-label="Theme feedback reaction"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}
          data-testid="theme-feedback-options"
        >
          {REACTIONS.map(({ key, emoji, label }) => {
            const isSelecting = selecting === key && submitMutation.isPending;
            return (
              <button
                type="button"
                key={key}
                role="radio"
                aria-checked={selecting === key}
                aria-label={label}
                onClick={() => handlePick(key)}
                disabled={submitMutation.isPending}
                data-testid={`theme-feedback-reaction-${key}`}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  padding: '14px 8px 10px',
                  background: 'var(--am-card)',
                  border: `1px solid ${isSelecting ? 'var(--am-emerald-500)' : 'var(--am-hair)'}`,
                  borderRadius: 'var(--am-radius)',
                  cursor: submitMutation.isPending ? 'wait' : 'pointer',
                  boxShadow: isSelecting ? '0 0 0 3px var(--am-emerald-ring)' : 'var(--am-shadow-1)',
                  transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                  opacity: submitMutation.isPending && !isSelecting ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (submitMutation.isPending) return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--am-shadow-2)';
                  e.currentTarget.style.borderColor = 'var(--am-hair-strong)';
                }}
                onMouseLeave={(e) => {
                  if (submitMutation.isPending) return;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--am-shadow-1)';
                  e.currentTarget.style.borderColor = 'var(--am-hair)';
                }}
              >
                <span style={{ fontSize: 30, lineHeight: 1 }} aria-hidden>{emoji}</span>
                <span
                  style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
                    textTransform: 'uppercase', color: 'var(--am-ink-2)',
                  }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        {submitMutation.isPending && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--am-ink-3)', textAlign: 'center' }}>
            Submitting…
          </p>
        )}
      </div>
    </div>
  );
}

export default ThemeFeedbackCard;
